/**
 * 订单流转场景适用
 */
import React, { useState, useEffect, useMemo, useRef, useImperativeHandle } from 'react';
import { Drawer } from 'antd';

import { getPortal } from '@/components/PortalContext';

import Header from './DrawerHeader';
import Footer from './DrawerFooter';
import styles from './index.module.scss';

interface IProps {
  ContentComponent: React.ComponentType<{ unmountNode: () => void }>;
  config: any;
  onClose: () => void;
  innerRef;
}

const DrawerWrapper: React.FC<IProps> = (props) => {
  const { ContentComponent, config, onClose, innerRef } = props;

  const [visible, setVisible] = useState(false);

  useEffect(() => {
    setVisible(true);
  }, []);

  useImperativeHandle(
    innerRef,
    () => ({
      closeModal: () => setVisible(false),
    }),
    [],
  );

  const handleVisibleChange = (value) => {
    if (!value) {
      onClose();
    }
  };

  const onChildrenDestroy = () => {
    setVisible(false);
  };

  return (
    <Drawer
      open={visible}
      closable={false}
      destroyOnClose
      className={styles.drawerContainer}
      afterOpenChange={handleVisibleChange}
    >
      <ContentComponent {...config} unmountNode={onChildrenDestroy} />
    </Drawer>
  );
};

const usePortalDrawer = <T,>(Comp) => {
  const modalRef = useRef();
  const modal = useMemo(() => {
    let nodeId: string;

    const closeDrawer = () => {
      if (nodeId) {
        getPortal().removePortalNode(nodeId);
      }
    };

    const showDrawer: (config?: T) => void = (config) => {
      nodeId = getPortal().renderPortalNode(() => (
        <DrawerWrapper
          ContentComponent={Comp}
          config={config}
          innerRef={modalRef}
          onClose={closeDrawer}
        />
      ));
    };

    return {
      showDrawer,
      closeDrawer: () => {
        const modalInst = modalRef.current;
        if (modalInst) {
          (modalInst as any).closeModal();
        }
      },
    };
  }, []);

  useEffect(() => {
    return () => {
      modal.closeDrawer();
    };
  }, [modal]);

  return modal;
};

export const DrawerHeader = Header;
export const DrawerFooter = Footer;
export default usePortalDrawer;
